"use client";

import React, { useState } from "react";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
} from "./ui/responsive-dialog";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import {
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const date = new Date(deadline);
  const now = new Date();
  const diffMs = date - now;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""}`;
  } else {
    return "Less than 1 hour";
  }
};

export default function ReassignmentApprovalModal({
  open,
  onOpenChange,
  reassignment,
  onAccept,
  onReject,
}) {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionType, setActionType] = useState(null); // "accept" or "reject"

  if (!reassignment) return null;

  const {
    originalTherapist,
    newTherapist,
    appointmentDate,
    decisionDeadline,
    clinic,
  } = reassignment;
  const timeRemaining = formatDeadline(decisionDeadline);
  const isUrgent = timeRemaining && timeRemaining.includes("Less than");

  const handleAccept = async () => {
    setIsProcessing(true);
    setActionType("accept");
    try {
      await onAccept(reassignment.bookingId);
      toast({
        title: "Reassignment accepted",
        description:
          "Your appointment has been confirmed with the new therapist.",
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message || "Failed to accept reassignment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setActionType(null);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    setActionType("reject");
    try {
      await onReject(reassignment.bookingId);
      toast({
        title: "Refund requested",
        description:
          "Your appointment has been cancelled and a full refund will be processed.",
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message || "Failed to process refund. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setActionType(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return "T";
    return (
      name
        .split(/\s+/)
        .map((part) => part?.[0]?.toUpperCase())
        .filter(Boolean)
        .slice(0, 2)
        .join("") || "T"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-amber-500" />
            Action Required: Appointment Reassignment
          </DialogTitle>
          <DialogDescription className="text-base">
            Your appointment has been reassigned to a different therapist.
            Please choose an option below.
          </DialogDescription>
        </DialogHeader>

        {/* Urgency Alert */}
        {isUrgent && timeRemaining && (
          <Card className="border-amber-500 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-amber-900 font-semibold">
                <Clock className="h-5 w-5" />
                <span>Time Remaining: {timeRemaining}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deadline Info */}
        {decisionDeadline && !isUrgent && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-blue-900">
                <Clock className="h-5 w-5" />
                <span>
                  Please respond by {formatDate(decisionDeadline)} at{" "}
                  {formatTime(decisionDeadline)}
                  {timeRemaining && ` (${timeRemaining} remaining)`}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appointment Details */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-lg mb-4">Appointment Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-palette-navy" />
                <span className="font-medium">
                  {formatDate(appointmentDate)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-palette-navy" />
                <span className="font-medium">
                  {formatTime(appointmentDate)}
                </span>
              </div>
              {clinic?.name && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-palette-navy" />
                  <span className="font-medium">{clinic.name}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Therapist Comparison */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Original Therapist */}
          <Card className="border-gray-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge variant="outline" className="bg-gray-100">
                  Previous Therapist
                </Badge>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="h-12 w-12">
                  {originalTherapist?.photo ? (
                    <img
                      src={originalTherapist.photo}
                      alt={originalTherapist.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <AvatarFallback className="bg-gray-200 text-gray-700">
                      {getInitials(originalTherapist?.name)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h4 className="font-semibold">
                    {originalTherapist?.name || "Previous Therapist"}
                  </h4>
                  {originalTherapist?.rating > 0 && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span>⭐</span>
                      <span>{originalTherapist.rating.toFixed(1)}</span>
                      {originalTherapist.reviewCount > 0 && (
                        <span className="text-gray-500">
                          ({originalTherapist.reviewCount} review
                          {originalTherapist.reviewCount !== 1 ? "s" : ""})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {originalTherapist?.massageTypes &&
                originalTherapist.massageTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {originalTherapist.massageTypes
                      .slice(0, 3)
                      .map((type, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {type}
                        </Badge>
                      ))}
                  </div>
                )}
            </CardContent>
          </Card>

          {/* New Therapist */}
          <Card className="border-palette-lime bg-palette-lime/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-palette-lime text-palette-navy">
                  New Therapist
                </Badge>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="h-12 w-12 border-2 border-palette-lime">
                  {newTherapist?.photo ? (
                    <img
                      src={newTherapist.photo}
                      alt={newTherapist.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <AvatarFallback className="bg-palette-lime text-palette-navy">
                      {getInitials(newTherapist?.name)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h4 className="font-semibold">
                    {newTherapist?.name || "New Therapist"}
                  </h4>
                  {newTherapist?.rating > 0 && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span>⭐</span>
                      <span>{newTherapist.rating.toFixed(1)}</span>
                      {newTherapist.reviewCount > 0 && (
                        <span className="text-gray-500">
                          ({newTherapist.reviewCount} review
                          {newTherapist.reviewCount !== 1 ? "s" : ""})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {newTherapist?.massageTypes &&
                newTherapist.massageTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newTherapist.massageTypes.slice(0, 3).map((type, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white">
                        {type}
                      </Badge>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </div>

        {/* Reassignment Reason */}
        {reassignment.reassignmentReason && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2">Reason for Reassignment</h4>
              <p className="text-sm text-gray-700">
                {reassignment.reassignmentReason}
              </p>
            </CardContent>
          </Card>
        )}

        <Separator />

        {/* Information Boxes */}
        <div className="space-y-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">
                    If you accept the reassignment:
                  </h4>
                  <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                    <li>
                      Your appointment will be confirmed with{" "}
                      {newTherapist?.name}
                    </li>
                    <li>Same date and time</li>
                    <li>No refund will be processed</li>
                    <li>You'll receive confirmation email</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">
                    If you request a refund:
                  </h4>
                  <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                    <li>Your appointment will be cancelled</li>
                    <li>Full refund to your original payment method</li>
                    <li>Refund processed within 5-10 business days</li>
                    <li>You'll receive refund confirmation email</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            onClick={handleAccept}
            disabled={isProcessing}
            className={cn(
              "flex-1 bg-green-600 hover:bg-green-700 text-white",
              isProcessing && actionType !== "accept" && "opacity-50",
            )}
            size="lg">
            {isProcessing && actionType === "accept" ? (
              "Processing..."
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Accept Reassignment
              </>
            )}
          </Button>
          <Button
            onClick={handleReject}
            disabled={isProcessing}
            variant="outline"
            className={cn(
              "flex-1 border-red-300 text-red-700 hover:bg-red-50",
              isProcessing && actionType !== "reject" && "opacity-50",
            )}
            size="lg">
            {isProcessing && actionType === "reject" ? (
              "Processing..."
            ) : (
              <>
                <XCircle className="h-5 w-5 mr-2" />
                Request Refund
              </>
            )}
          </Button>
        </div>

        {/* Warning about automatic refund */}
        {decisionDeadline && (
          <p className="text-xs text-gray-500 text-center pt-2">
            If no action is taken by {formatDate(decisionDeadline)} at{" "}
            {formatTime(decisionDeadline)}, a full refund will be processed
            automatically.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
